import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPT FOR hero-main-*.jpg:
 * "A confident Indian student (age 17-18) sitting at a modern desk with laptop, 
 * surrounded by floating holographic AI elements showing physics formulas, chemistry molecules, 
 * and mathematical equations. Warm lighting, professional photography style, inspiring and 
 * futuristic atmosphere. The student is smiling confidently with books and notes visible. 
 * Blue and purple AI glow effects. High quality, 8K, photorealistic."
 */

const HeroSectionV2: React.FC = () => {
  const [heroImage, setHeroImage] = useState<string>('');

  useEffect(() => {
    // Randomly select one of the 5 hero images
    const randomIndex = Math.floor(Math.random() * 5) + 1;
    setHeroImage(`/img/home-v2/hero-main-${randomIndex}.jpg`);
  }, []);
  return (
    <div className="hero-section-v2 position-relative overflow-hidden" 
      style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '50px',
        paddingBottom: '100px',
      }}>
      
      {/* Animated Background Shapes */}
      <div className="position-absolute w-100 h-100" style={{ opacity: 0.1 }}>
        <div className="position-absolute rounded-circle" 
          style={{ 
            width: '500px', 
            height: '500px', 
            background: 'white',
            top: '-10%',
            right: '-5%',
            animation: 'float 6s ease-in-out infinite'
          }}></div>
        <div className="position-absolute rounded-circle" 
          style={{ 
            width: '300px', 
            height: '300px', 
            background: 'white',
            bottom: '-5%',
            left: '-3%',
            animation: 'float 8s ease-in-out infinite reverse'
          }}></div>
      </div>

      <div className="container position-relative">
        <div className="row align-items-center g-5">
          
          {/* Left Content */}
          <div className="col-lg-6 text-white">
            <div className="animate__animated animate__fadeInLeft">
              
              {/* Badge */}
              <div className="d-inline-flex align-items-center px-4 py-2 mb-4 rounded-pill"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                <h1 className="fw-semibold" style={{fontSize: '1.25rem', margin: 0}}>Best JEE/ NEET preparation app</h1>
              </div>

              {/* Main Heading */}
              <h1 className="display-2 fw-bold mb-4" style={{ lineHeight: '1.2' }}>
                Master <span style={{ 
                  background: 'linear-gradient(to right, #fff, #ffd700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>JEE & NEET</span> with WiZiAi
              </h1>
              <h2 style={{fontSize: '1.25rem', margin: 0}}>NEET Preparation App</h2>  
              
              {/* Subheading */}
              <p className="lead mb-4 fs-4" style={{ opacity: 0.95 }}>
                Achieve your IIT and AIIMS dreams with personalized AI-powered 
                preparation, instant doubt solving, and comprehensive performance 
                tracking on India's newest smart learning platform.
              </p>

              {/* Key Features List */}
              <div className="row g-3 mb-5">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: '50px',
                          height: '50px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)'
                        }}>
                        <i className="fas fa-brain fa-lg"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0 fw-semibold">AI Performance Analysis</h6>
                      <small style={{ opacity: 0.9 }}>100+ detailed metrics</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: '50px',
                          height: '50px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)'
                        }}>
                        <i className="fas fa-microphone-alt fa-lg"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0 fw-semibold">Interactive AI Mentor</h6>
                      <small style={{ opacity: 0.9 }}>24/7 instant help</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: '50px',
                          height: '50px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)'
                        }}>
                        <i className="fas fa-bolt fa-lg"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0 fw-semibold">Instant Results</h6>
                      <small style={{ opacity: 0.9 }}>Real-time scoring</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: '50px',
                          height: '50px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)'
                        }}>
                        <i className="fas fa-chart-line fa-lg"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0 fw-semibold">Smart Mock Tests</h6>
                      <small style={{ opacity: 0.9 }}>Adaptive difficulty</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="d-flex flex-wrap gap-3">
                <Link to="/signup" 
                  className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg"
                  style={{
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}>
                  <i className="fas fa-rocket me-2"></i>
                  Start Free Trial
                </Link>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.wiziai.student&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-light btn-lg px-5 py-3 rounded-pill"
                  style={{
                    borderWidth: '2px',
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}>
                  <i className="fab fa-google-play me-2"></i>
                  Download App
                </a>
                {/* <Link to="/demo" 
                  className="btn btn-outline-light btn-lg px-5 py-3 rounded-pill"
                  style={{
                    borderWidth: '2px',
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}>
                  <i className="fas fa-play-circle me-2"></i>
                  Watch Demo
                </Link> */}
              </div>

              {/* Trust Indicators */}
              {/* <div className="d-flex flex-wrap gap-4 mt-5 pt-4" 
                style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-shield-alt text-success fa-lg me-2"></i>
                    <span className="fw-semibold">Trusted Platform</span>
                  </div>
                  <small style={{ opacity: 0.8 }}>Secure & Reliable</small>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '1rem' }}>
                  <div className="fw-semibold fs-5">24/7</div>
                  <small style={{ opacity: 0.8 }}>Support</small>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '1rem' }}>
                  <div className="fw-semibold fs-5">100%</div>
                  <small style={{ opacity: 0.8 }}>Free Trial</small>
                </div>
              </div> */}
            </div>
          </div>

          {/* Right Image */}
          <div className="col-lg-6">
            <div className="animate__animated animate__fadeInRight">
              <div className="position-relative">
                {/* Main Image Container with Glassmorphism */}
                <div className="rounded-4 overflow-hidden shadow-lg"
                  style={{
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(20px)',
                    background: 'rgba(255, 255, 255, 0.1)'
                  }}>
                  
                  {/* Hero Image */}
                  {heroImage && (
                    <img 
                      src={heroImage} 
                      alt="WiziAI Student Success" 
                      className="img-fluid"
                      style={{ width: '100%', height: '600px', objectFit: 'cover' }}
                    />
                  )}
                </div>

                {/* Floating Stats Cards */}
                <div className="position-absolute" 
                  style={{ top: '20px', right: '-20px', animation: 'float 4s ease-in-out infinite' }}>
                  <div className="rounded-3 p-3 shadow-lg"
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      minWidth: '200px'
                    }}>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fas fa-brain fa-2x text-primary"></i>
                      </div>
                      <div>
                        <div className="fw-bold fs-4 text-primary">100K+</div>
                        <small className="text-muted">Questions</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="position-absolute" 
                  style={{ bottom: '30px', left: '-30px', animation: 'float 5s ease-in-out infinite reverse' }}>
                  <div className="rounded-3 p-3 shadow-lg"
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      minWidth: '200px'
                    }}>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fas fa-robot fa-2x" style={{ color: '#764ba2' }}></i>
                      </div>
                      <div>
                        <div className="fw-bold fs-4" style={{ color: '#764ba2' }}>AI Mentor</div>
                        <small className="text-muted">24/7 Available</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="position-absolute w-100 text-center" style={{ bottom: '30px' }}>
        <div className="text-white" style={{ animation: 'bounce 2s infinite' }}>
          <div className="mb-2" style={{ opacity: 0.8 }}>Scroll to explore</div>
          <i className="fas fa-chevron-down fa-2x"></i>
        </div>
      </div>
    </div>
  );
};

export default HeroSectionV2;
